import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatLongDate, todayISO, WEEKDAYS_IT } from "@/lib/format";
import { AdminButton, AdminCard, AdminField, EmptyState, adminInput } from "@/components/admin/ui";

const DAY_OPTIONS = [
  ["Chiuso", "closed"],
  ["Chiuso per evento privato", "closed"],
  ["Chiuso per motivi tecnici", "closed"],
  ["Chiuso per ferie", "closed"],
  ["Apertura straordinaria", "open"],
];

function EccezioneGiorno() {
  const [date, setDate] = useState(todayISO());
  const [scelta, setScelta] = useState("Chiuso");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [error, setError] = useState("");

  const salva = async () => {
    setBusy(true);
    setOk("");
    setError("");
    const apri = scelta === "Apertura straordinaria";
    try {
      await api.put("/admin/day-open", { date, open: apri, reason: apri ? "" : scelta });
      setOk(
        apri
          ? `${formatLongDate(date)}: aperto in via straordinaria, con gli orari del giorno di apertura più vicino.`
          : `${formatLongDate(date)}: ${scelta.toLowerCase()}.`,
      );
      window.setTimeout(() => setOk(""), 7000);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminCard title="Chiusura o apertura straordinaria" testId="services-day-exception">
      <p className="mb-4 text-sm text-foreground/65">
        Vale per una data singola e ha la precedenza sul calendario settimanale. Le chiusure e le
        aperture straordinarie compaiono anche fra gli avvisi sul sito.
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        <AdminField label="Data">
          <input
            type="date"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className={adminInput}
            data-testid="exception-date"
          />
        </AdminField>
        <AdminField label="Cosa succede" className="sm:col-span-2">
          <select
            value={scelta}
            onChange={(e) => setScelta(e.target.value)}
            className={adminInput}
            data-testid="exception-type"
          >
            {DAY_OPTIONS.map(([label]) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
        </AdminField>
      </div>
      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      {ok && (
        <p className="mt-4 border border-sage/50 bg-sage/10 px-4 py-3 text-sm text-wood" data-testid="exception-ok">
          {ok}
        </p>
      )}
      <div className="mt-4">
        <AdminButton onClick={salva} disabled={busy} data-testid="exception-save">
          {busy ? "Salvataggio…" : "Salva eccezione"}
        </AdminButton>
      </div>
    </AdminCard>
  );
}

const NEW_SERVICE = {
  name: "Cena",
  start_time: "19:45",
  end_time: "23:00",
  slot_interval_minutes: 30,
  last_seating_offset_minutes: 90,
  capacity: 18,
  slot_capacity: "",
  max_online_party_size: 8,
  online_booking_enabled: true,
  active: true,
};

function ServiceForm({ service, weekday, onSaved, onCancel }) {
  const [form, setForm] = useState({ ...NEW_SERVICE, ...service, slot_capacity: service?.slot_capacity ?? "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setBusy(true);
    setError("");
    const payload = {
      weekday,
      name: form.name,
      start_time: form.start_time,
      end_time: form.end_time,
      slot_interval_minutes: Number(form.slot_interval_minutes),
      last_seating_offset_minutes: Number(form.last_seating_offset_minutes),
      capacity: Number(form.capacity),
      slot_capacity: form.slot_capacity === "" ? null : Number(form.slot_capacity),
      max_online_party_size: Number(form.max_online_party_size),
      online_booking_enabled: !!form.online_booking_enabled,
      active: !!form.active,
    };
    try {
      if (service?.id) await api.put(`/admin/services/${service.id}`, payload);
      else await api.post("/admin/services", payload);
      onSaved();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-sage/40 bg-sage/5 p-4" data-testid="service-form">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <AdminField label="Nome servizio">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={adminInput} data-testid="service-name" />
        </AdminField>
        <AdminField label="Inizio">
          <input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={adminInput} placeholder="19:45" data-testid="service-start" />
        </AdminField>
        <AdminField label="Fine">
          <input value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={adminInput} placeholder="23:30" data-testid="service-end" />
        </AdminField>
        <AdminField label="Intervallo (min)">
          <input type="number" min="5" value={form.slot_interval_minutes} onChange={(e) => setForm({ ...form, slot_interval_minutes: e.target.value })} className={adminInput} data-testid="service-interval" />
        </AdminField>
        <AdminField label="Ultimo ingresso (min prima della fine)">
          <input type="number" min="0" value={form.last_seating_offset_minutes} onChange={(e) => setForm({ ...form, last_seating_offset_minutes: e.target.value })} className={adminInput} />
        </AdminField>
        <AdminField label="Coperti totali">
          <input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className={adminInput} data-testid="service-capacity" />
        </AdminField>
        <AdminField label="Coperti per fascia (facoltativo)">
          <input type="number" min="1" value={form.slot_capacity} onChange={(e) => setForm({ ...form, slot_capacity: e.target.value })} className={adminInput} />
        </AdminField>
        <AdminField label="Max persone online">
          <input type="number" min="1" value={form.max_online_party_size} onChange={(e) => setForm({ ...form, max_online_party_size: e.target.value })} className={adminInput} />
        </AdminField>
      </div>
      <div className="mt-3 flex flex-wrap gap-5">
        <label className="flex items-center gap-2 text-sm text-foreground/70">
          <input type="checkbox" checked={!!form.online_booking_enabled} onChange={(e) => setForm({ ...form, online_booking_enabled: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--sage))]" data-testid="service-online" />
          Prenotazione online attiva
        </label>
        <label className="flex items-center gap-2 text-sm text-foreground/70">
          <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--sage))]" />
          Servizio attivo
        </label>
      </div>
      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      <div className="mt-4 flex gap-2">
        <AdminButton onClick={save} disabled={busy} data-testid="service-save">
          Salva
        </AdminButton>
        <AdminButton variant="ghost" onClick={onCancel}>
          Annulla
        </AdminButton>
      </div>
    </div>
  );
}

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null); // {weekday, service}
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/services");
      setServices(data);
    } catch (err) {
      setError(apiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/services/${id}`);
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-services">
      <div>
        <p className="text-[0.62rem] uppercase tracking-[0.2em] text-sage">Configurazione</p>
        <h1 className="mt-2 font-serif text-3xl md:text-4xl text-wood">Servizi e orari</h1>
        <p className="mt-3 max-w-2xl text-sm text-foreground/65">
          Ogni giorno può avere più servizi (pranzo, cena). Gli orari prenotabili online vengono generati
          da queste impostazioni.
        </p>
      </div>

      {error && <p className="border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">{error}</p>}

      <EccezioneGiorno />

      <div className="space-y-4">
        {WEEKDAYS_IT.map((day, idx) => {
          const list = services.filter((s) => s.weekday === idx);
          return (
            <AdminCard
              key={day}
              title={day}
              testId={`weekday-${idx}`}
              action={
                <AdminButton variant="outline" onClick={() => setEditing({ weekday: idx, service: null })} data-testid={`add-service-${idx}`}>
                  <Plus size={13} /> Servizio
                </AdminButton>
              }
            >
              {list.length === 0 && !(editing?.weekday === idx && !editing.service) && (
                <p className="text-sm text-foreground/55">Chiuso</p>
              )}
              <div className="space-y-3">
                {list.map((svc) =>
                  editing?.service?.id === svc.id ? (
                    <ServiceForm
                      key={svc.id}
                      service={svc}
                      weekday={idx}
                      onCancel={() => setEditing(null)}
                      onSaved={() => {
                        setEditing(null);
                        load();
                      }}
                    />
                  ) : (
                    <div key={svc.id} className="flex flex-wrap items-center gap-x-5 gap-y-2 border border-border px-4 py-3" data-testid={`service-${svc.id}`}>
                      <span className="font-serif text-lg text-wood">{svc.name}</span>
                      <span className="text-sm text-foreground/70 tabular-nums">
                        {svc.start_time} – {svc.end_time}
                      </span>
                      <span className="text-xs text-foreground/50">
                        ogni {svc.slot_interval_minutes} min · {svc.capacity} coperti · max {svc.max_online_party_size} online
                      </span>
                      {!svc.online_booking_enabled && (
                        <span className="text-[0.6rem] uppercase tracking-[0.16em] text-gold">solo telefono</span>
                      )}
                      {!svc.active && (
                        <span className="text-[0.6rem] uppercase tracking-[0.16em] text-destructive">disattivo</span>
                      )}
                      <div className="ml-auto flex gap-2">
                        <AdminButton variant="ghost" onClick={() => setEditing({ weekday: idx, service: svc })} data-testid={`edit-service-${svc.id}`}>
                          Modifica
                        </AdminButton>
                        <AdminButton variant="danger" onClick={() => remove(svc.id)} data-testid={`delete-service-${svc.id}`}>
                          <Trash2 size={13} />
                        </AdminButton>
                      </div>
                    </div>
                  ),
                )}
                {editing?.weekday === idx && !editing.service && (
                  <ServiceForm
                    weekday={idx}
                    onCancel={() => setEditing(null)}
                    onSaved={() => {
                      setEditing(null);
                      load();
                    }}
                  />
                )}
              </div>
            </AdminCard>
          );
        })}
      </div>
    </div>
  );
}
